import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from './Login.module.css';
import UserService from '../../API/UserService';
import { useLoader } from '../../hooks/useLoader';
import { useAuth } from '../../context/AuthContext';
import YandexLoginButton from '../../components/OAuth/YandexLoginButton';
import GoogleLoginButton from '../../components/OAuth/GoogleLoginButton';

// Вход в АдминПанель.
// Регистрации тут нет — она идёт через startklass.ru/registration.
// Здесь — одноэкранник: слева инфо-блок «Что такое АдминПанель» с фишками
// и кнопкой регистрации (внешняя ссылка), справа форма входа.

// Список преимуществ продукта, видимых на этой странице.
// Это первое, что видит руководитель центра при заходе — должно убеждать
// в ценности продукта за 5 секунд.
const FEATURES = [
    {
        title: 'Журнал и оплаты',
        text: 'Посещения, абонементы, мероприятия — в одном месте. Без Excel и Telegram-таблиц.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="17" rx="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 2v4M16 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 15l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        title: 'Подключение Майнкрафтии и РобоТехники',
        text: 'Один клик — и ученик получает доступ ко всем 35 курсам без своей подписки.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L4 7v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V7l-8-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
    {
        title: 'Дашборд с метриками',
        text: 'Выручка, средний чек, посещаемость, популярные курсы — всё видно сразу.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="13" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="10" y="8" width="4" height="13" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="17" y="3" width="4" height="18" rx="1" stroke="currentColor" strokeWidth="2"/>
            </svg>
        ),
    },
    {
        title: 'Проверка проектов РобоТехники',
        text: 'Ребёнок сдал — преподаватель принял в один клик, алмазы автоматически.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
    },
];

const Login = () => {
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const navigate = useNavigate();
    const { login: authLogin } = useAuth();

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleServerError = (msg) => {
        setServerError(msg);
        setTimeout(() => setServerError(''), 5000);
    };
    const handleSuccess = (msg) => {
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

    const [loginFetch, isLoginLoading] = useLoader(async () => {
        const newErrors = {};
        if (!loginData.email) newErrors.email = 'Email обязателен';
        else if (!validateEmail(loginData.email)) newErrors.email = 'Некорректный email';
        if (!loginData.password) newErrors.password = 'Пароль обязателен';
        else if (loginData.password.length < 8) newErrors.password = 'Пароль минимум 8 символов';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            throw new Error('Проверьте введенные данные');
        }

        // 1) Авторизуемся в user-микросервисе. UserService.login кладёт токен
        // в localStorage сам и вызывает success-callback.
        await new Promise((resolve, reject) => {
            UserService.login(
                loginData,
                () => resolve(),
                (error) => reject(new Error(error)),
            );
        });

        const token = localStorage.getItem('Authorization');
        if (!token) {
            throw new Error('Сервер не вернул токен');
        }

        // 2) Прямой fetch на access-check — БЕЗ axios-interceptor который
        // на любой 401 чистит localStorage и редиректит на /login.
        // (Это была причина гонки: getMyProfile в AuthContext.useEffect
        // отвечал быстрее access-check и при сетевом 401 ронял токен.)
        //
        // 2026-05-31: если admin-сервис отдал 401 (его JWT-секрет не
        // совпадает с user-сервисом) или 5xx (лежит) — НЕ кидаем
        // юзера на /no-access. AuthProvider.init сделает fallback на
        // /users/profile и пустит admin'ов по role.
        // На /no-access кидаем ТОЛЬКО если access-check явно ответил
        // 200 с has_access=false (это означает «юзер существует, но
        // он не админ/не владелец центра» — это правильное поведение).
        let hasAccess = true;
        try {
            const ac = await fetch(
                'https://minecraftia-school.ru/api-admin/api/v1/admin/access-check',
                { headers: { 'Authorization': token } },
            );
            if (ac.ok) {
                const acData = await ac.json();
                hasAccess = Boolean(acData?.has_access);
            }
            // не-200 (401/403/5xx) — оставляем hasAccess=true, фолбэк в init
        } catch (e) {
            // Сетевая ошибка — пускаем дальше, AuthProvider.init разберётся
            hasAccess = true;
        }

        handleSuccess('Успешный вход!');
        // Жёсткий редирект: SPA перезагрузится с уже установленным токеном,
        // AuthProvider.init спокойно прокачает профиль+доступ без гонок.
        setTimeout(() => {
            if (!hasAccess) {
                window.location.replace('/no-access');
            } else {
                window.location.replace('/main');
            }
        }, 400);
    });

    const handleLogin = async (e) => {
        e.preventDefault();
        setServerError('');
        setSuccessMessage('');
        try {
            await loginFetch();
        } catch (err) {
            handleServerError(err.message);
        }
    };

    const handleLoginChange = (e) => {
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
    };

    return (
        <div className={cl.shell}>
            {/* === ВЕРХНЯЯ ШАПКА — простая тёмная полоса с логотипом === */}
            <header className={cl.topBar}>
                <a
                    href="https://startklass.ru"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cl.topBarBrand}
                >
                    <span className={cl.topBarLogo}>SK</span>
                    <span className={cl.topBarName}>СтартКласс</span>
                </a>
            </header>

            <div className={cl.page}>
            {/* === ЛЕВАЯ ПАНЕЛЬ — БРЕНДОВЫЙ ИНФО-БЛОК === */}
            <aside className={cl.left}>
                {/* Анимированные пятна на фоне для глубины */}
                <div className={`${cl.blob} ${cl.blob1}`}/>
                <div className={`${cl.blob} ${cl.blob2}`}/>
                <div className={`${cl.blob} ${cl.blob3}`}/>

                <div className={cl.leftInner}>
                    {/* Логотип теперь живёт в верхней шапке (.topBar) — здесь не дублируем */}
                    <div className={cl.heroBlock}>
                        <h1 className={cl.heroTitle}>
                            АдминПанель <span className={cl.heroAccent}>детского центра</span>
                        </h1>
                        <p className={cl.heroLead}>
                            Управляйте журналом, оплатами, учениками и подключайте их к
                            Майнкрафтии и РобоТехнике из одного окна.
                        </p>
                    </div>

                    <div className={cl.features}>
                        {FEATURES.map(f => (
                            <div key={f.title} className={cl.feature}>
                                <span className={cl.featureIcon}>{f.icon}</span>
                                <div>
                                    <div className={cl.featureTitle}>{f.title}</div>
                                    <div className={cl.featureText}>{f.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={cl.footer}>
                        <span>Ещё нет центра в системе?</span>
                        <a
                            href="https://startklass.ru/registration"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cl.regLink}
                        >
                            Зарегистрировать на startklass.ru →
                        </a>
                    </div>
                </div>
            </aside>

            {/* === ПРАВАЯ ПАНЕЛЬ — ФОРМА ВХОДА === */}
            <main className={cl.right}>
                <div className={cl.formCard}>
                    <div className={cl.formHead}>
                        <h2 className={cl.formTitle}>Войти в АдминПанель</h2>
                        <p className={cl.formSub}>
                            Используйте email и пароль вашего аккаунта руководителя
                            или преподавателя.
                        </p>
                    </div>

                    {serverError && (
                        <div className={cl.serverError}>{serverError}</div>
                    )}
                    {successMessage && (
                        <div className={cl.successMessage}>{successMessage}</div>
                    )}

                    {/* OAuth-вход. После него access-check админки сделает
                        авторедирект на /main или /no-access. */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                        <YandexLoginButton disabled={isLoginLoading} />
                        <GoogleLoginButton disabled={isLoginLoading} />
                        <div style={{
                            textAlign: 'center',
                            margin: '6px 0 -2px',
                            color: '#94a3b8',
                            fontSize: 13,
                            position: 'relative',
                        }}>
                            <span style={{
                                background: 'var(--login-card-bg, #fff)',
                                padding: '0 12px',
                                position: 'relative',
                                zIndex: 1,
                            }}>
                                или войти по e-mail
                            </span>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: 0, right: 0,
                                height: 1,
                                background: '#e6e7eb',
                                zIndex: 0,
                            }} />
                        </div>
                    </div>

                    <form onSubmit={handleLogin} className={cl.form}>
                        <div className={cl.inputGroup}>
                            <label className={cl.inputLabel}>Email</label>
                            <input
                                type="email"
                                name="email"
                                placeholder="you@example.com"
                                value={loginData.email}
                                onChange={handleLoginChange}
                                className={`${cl.input} ${errors.email ? cl.error : ''}`}
                                disabled={isLoginLoading}
                                autoComplete="email"
                            />
                            {errors.email && <span className={cl.errorText}>{errors.email}</span>}
                        </div>

                        <div className={cl.inputGroup}>
                            <label className={cl.inputLabel}>Пароль</label>
                            <input
                                type="password"
                                name="password"
                                placeholder="Минимум 8 символов"
                                value={loginData.password}
                                onChange={handleLoginChange}
                                className={`${cl.input} ${errors.password ? cl.error : ''}`}
                                disabled={isLoginLoading}
                                autoComplete="current-password"
                            />
                            {errors.password && <span className={cl.errorText}>{errors.password}</span>}
                        </div>

                        <button
                            type="submit"
                            className={`${cl.submitBtn} ${isLoginLoading ? cl.loading : ''}`}
                            disabled={isLoginLoading}
                        >
                            {isLoginLoading ? 'Вход…' : 'Войти'}
                        </button>
                    </form>

                    {/* Дублирующий блок регистрации — для тех, кто смотрит сразу на форму */}
                    <div className={cl.altReg}>
                        Нет аккаунта центра?{' '}
                        <a
                            href="https://startklass.ru/registration"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Зарегистрироваться
                        </a>
                    </div>
                </div>
            </main>
            </div>
        </div>
    );
};

export default Login;
